import React, {
  createContext,
  useContext,
  useState,
  FC,
  PropsWithChildren,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import palette from '~/theme/palette';
import { matchRecordPage } from '~/utils/recordPages';

export type EditMode = 'VIEW' | 'DRAFT' | 'CHANGELOG';

interface EditModeContextProps {
  editMode: EditMode;
  setEditMode: (state: EditMode) => void;
}

const getAvailableEditModes = (pathname: string) => {
  const page = matchRecordPage(pathname);
  return { draft: !!(page?.drafts && page.isDraft), changelog: !!page?.changelog };
};

const UseEditModeContext = createContext<EditModeContextProps>({
  editMode: 'VIEW',
  setEditMode: () => {},
});

export const EditModeProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [editMode, setEditMode] = useState<EditMode>('VIEW');

  const location = useLocation();
  const currentPathname = location.pathname;

  useEffect(() => {
    setEditMode((current) => {
      const available = getAvailableEditModes(currentPathname);
      if (available.draft) return 'DRAFT';
      if (available.changelog && current === 'CHANGELOG') return 'CHANGELOG';
      return 'VIEW';
    });
  }, [currentPathname]);

  return (
    <UseEditModeContext.Provider value={{ editMode, setEditMode }}>
      {children}
    </UseEditModeContext.Provider>
  );
};

export const useEditMode = (): EditModeContextProps => {
  return useContext(UseEditModeContext);
};

const EDIT_MODE_COLORS: Partial<Record<EditMode, { base: string; hint: string }>> = {
  CHANGELOG: { base: palette.changelog.base, hint: palette.warning.main },
  DRAFT: { base: palette.draft.base, hint: palette.success.main },
};

export const getBaseEditModeColor = (editMode: EditMode) => EDIT_MODE_COLORS[editMode]?.base;
export const getHintEditModeColor = (editMode: EditMode) => EDIT_MODE_COLORS[editMode]?.hint;

export const useEditModeStyles = makeStyles(() => ({
  backgroundColor: ({ editMode }: { editMode: EditMode }) => ({
    backgroundColor: getBaseEditModeColor(editMode),
    // border: `10px solid ${getBaseEditModeColor(editMode)}`,
  }),
}));

const useEditModeTableStyles = makeStyles(() => ({
  tableDraft: {
    backgroundColor: palette.draft.baseDarker,
    '& > thead > tr:last-child': { borderColor: palette.draft.baseEvenDarker },
    '& > tbody > tr': { borderColor: palette.draft.baseEvenDarker },
  },
  tableChangelog: {
    backgroundColor: palette.changelog.baseDarker,
    '& > thead > tr:last-child': { borderColor: palette.changelog.baseEvenDarker },
    '& > tbody > tr': { borderColor: palette.changelog.baseEvenDarker },
  },
  rowDraft: { '&:hover': { backgroundColor: `${palette.draft.baseHover} !important` } },
  rowChangelog: { '&:hover': { backgroundColor: `${palette.changelog.baseHover} !important` } },
}));

const useEditModeAccordionStyles = makeStyles(() => ({
  accordionDraft: {
    '&.MuiAccordion-root': {
      backgroundColor: palette.draft.base,
      '&:before': { backgroundColor: palette.draft.baseEvenDarker },
    },
  },
  accordionChangelog: {
    '&.MuiAccordion-root': {
      backgroundColor: palette.changelog.base,
      '&:before': { backgroundColor: palette.changelog.baseEvenDarker },
    },
  },
}));

/** Edit mode class for an <Accordion>. Undefined in VIEW mode. */
export const useEditModeAccordionClass = (): string | undefined => {
  const { editMode } = useEditMode();
  const classes = useEditModeAccordionStyles();

  return {
    DRAFT: classes.accordionDraft,
    CHANGELOG: classes.accordionChangelog,
    VIEW: undefined,
  }[editMode];
};

export const useEditModeTableClasses = (): { table?: string; row?: string } => {
  const { editMode } = useEditMode();
  const classes = useEditModeTableStyles();

  return {
    table: { DRAFT: classes.tableDraft, CHANGELOG: classes.tableChangelog, VIEW: undefined }[
      editMode
    ],
    row: { DRAFT: classes.rowDraft, CHANGELOG: classes.rowChangelog, VIEW: undefined }[editMode],
  };
};
